import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { handleMediaDownload } from "../../../appStore/actions/ArkchatGroup/action";
import { getCloudStoragePreviewUrl } from "../../../appStore/actions/cloudStorage/action";
import { getFileIconUrl } from "../../../utils/findextension";
import filesvg from "../../../assets/images/chats/file_svg.svg";
import download from "../../../assets/images/downloadLogo.svg";

// Mime prefixes routed through Google's viewer since browsers can't render
// Office formats natively the way they do images/PDFs.
const OFFICE_MIME_PREFIXES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
];

const classes = {
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "auto",
    padding: "0.7rem",
  },
  actionBtn: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    cursor: "pointer",
    border: "1px solid #000",
    borderRadius: "0.15rem",
    padding: "0.3rem 0.7rem",
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
  },
  floatingDownloadBtn: {
    position: "absolute",
    top: "0.6rem",
    right: "0.6rem",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "1px solid #000",
    borderRadius: "0.15rem",
    padding: "0.3rem",
    backgroundColor: "#FDFDFD",
  },
};

// Link-type attachments (no mimeType, see MediaFiles.jsx) carry a real
// external URL — only YouTube and Drive "view" links have a documented
// embeddable form; anything else falls back to "open in a new tab".
const getYouTubeEmbedUrl = (location) => {
  try {
    const url = new URL(location);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      return `https://www.youtube.com/embed${url.pathname}`;
    }
    if (host.endsWith("youtube.com")) {
      const videoId = url.searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
  } catch {
    // not a parseable URL
  }
  return null;
};

const getDriveEmbedUrl = (location) => {
  try {
    const url = new URL(location);
    if (url.hostname.includes("drive.google.com") && /\/view$/.test(url.pathname)) {
      return location.replace(/\/view(?=(\?|$))/, "/preview");
    }
  } catch {
    // not a parseable URL
  }
  return null;
};

const FilePreview = ({ file }) => {
  const dispatch = useDispatch();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);

  const mimeType = file?.mimeType || "";
  // Files picked from a connected Google Drive/OneDrive that haven't been
  // imported into Arkchat storage yet (see CloudStorageDrawer.jsx) carry no
  // `location`/`mimeType` — they're always embedded via the provider's own
  // preview viewer (see getCloudStoragePreviewUrl) instead of routed through
  // the mimeType-based renderers below, since that viewer already handles
  // every format the provider knows how to render.
  const cloudProvider = file?.cloudPreview?.provider;
  const cloudFileId = file?.cloudPreview?.fileId;
  const cloudDriveId = file?.cloudPreview?.driveId;
  const isCloudPreview = Boolean(cloudFileId);
  const isLinkItem = !mimeType && !isCloudPreview;
  const embedUrl = isLinkItem
    ? getYouTubeEmbedUrl(file?.location) || getDriveEmbedUrl(file?.location)
    : null;

  useEffect(() => {
    setPreviewUrl(null);
    setError(false);
    if (isCloudPreview) {
      setLoading(true);
      getCloudStoragePreviewUrl(cloudProvider, {
        fileId: cloudFileId,
        driveId: cloudDriveId,
      })
        .then((res) => {
          if (res?.url) {
            setPreviewUrl(res.url);
          } else {
            setError(true);
          }
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
      return;
    }
    if (!file?.location || !mimeType) {
      return;
    }
    setLoading(true);
    dispatch(handleMediaDownload({ location: file.location, action: "" }))
      .then((res) => {
        if (res?.data) {
          setPreviewUrl(res.data);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [dispatch, file?.location, mimeType, isCloudPreview, cloudProvider, cloudFileId, cloudDriveId]);

  const handleDownloadClick = () => {
    dispatch(
      handleMediaDownload({ location: file?.location, action: "download" }),
    ).then((dataUrl) => {
      const url = window.URL.createObjectURL(dataUrl);
      const a = document.createElement("a");
      a.href = url;
      a.download = file?.name || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    });
  };

  const renderBody = () => {
    if (isCloudPreview) {
      if (isLoading) {
        return <CircularProgress size={28} />;
      }
      if (hasError || !previewUrl) {
        return (
          <Stack alignItems="center" spacing={1.5}>
            <img src={filesvg} alt="file" style={{ width: 56 }} />
            <Typography fontSize="0.85rem">Preview unavailable.</Typography>
          </Stack>
        );
      }
      return (
        <iframe
          title={file?.name}
          src={previewUrl}
          style={{ width: "100%", height: "100%", border: "none" }}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      );
    }

    if (isLinkItem) {
      if (embedUrl) {
        return (
          <iframe
            title={file?.name}
            src={embedUrl}
            style={{ width: "100%", height: "100%", border: "none" }}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        );
      }
      return (
        <Stack alignItems="center" spacing={1.5}>
          <img
            src={getFileIconUrl(file?.location || file?.name)}
            alt="link"
            style={{ width: 56, height: 56 }}
          />
          <Typography fontSize="0.85rem" textAlign="center">
            This link can&apos;t be previewed here.
          </Typography>
          <Box
            sx={classes.actionBtn}
            onClick={() => window.open(file?.location, "_blank")}
          >
            Open link
          </Box>
        </Stack>
      );
    }

    if (isLoading) {
      return <CircularProgress size={28} />;
    }

    if (hasError || !previewUrl) {
      return (
        <Stack alignItems="center" spacing={1.5}>
          <img src={filesvg} alt="file" style={{ width: 56 }} />
          <Typography fontSize="0.85rem">Preview unavailable.</Typography>
        </Stack>
      );
    }

    if (mimeType.startsWith("image/")) {
      return (
        <img
          src={previewUrl}
          alt={file?.name}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      );
    }
    if (mimeType.startsWith("video/")) {
      return (
        <video
          controls
          src={previewUrl}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      );
    }
    if (mimeType.startsWith("audio/")) {
      return <audio controls src={previewUrl} style={{ width: "100%" }} />;
    }
    if (mimeType === "application/pdf") {
      return (
        <iframe
          title={file?.name}
          src={previewUrl}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      );
    }
    if (OFFICE_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix))) {
      return (
        <iframe
          title={file?.name}
          src={`https://docs.google.com/gview?url=${encodeURIComponent(previewUrl)}&embedded=true`}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      );
    }

    return (
      <Stack alignItems="center" spacing={1.5}>
        <img
          src={getFileIconUrl(file?.location || file?.name)}
          alt="file"
          style={{ width: 56, height: 56 }}
        />
        <Typography fontSize="0.85rem" textAlign="center">
          No inline preview available for this file type.
        </Typography>
        <Box sx={classes.actionBtn} onClick={handleDownloadClick}>
          <img src={download} alt="download" style={{ width: 16, height: 16 }} />
          Download
        </Box>
      </Stack>
    );
  };

  return (
    <Stack sx={{ bgcolor: "#FDFDFD", height: "100%", overflow: "hidden", position: "relative" }}>
      {!isLinkItem && !isCloudPreview && (
        <Box sx={classes.floatingDownloadBtn} onClick={handleDownloadClick} title="Download">
          <img src={download} alt="download" style={{ width: 16, height: 16 }} />
        </Box>
      )}
      <Box sx={classes.body}>{renderBody()}</Box>
    </Stack>
  );
};

export default FilePreview;
